<script setup lang="ts">
import { TimesIcon } from "@voltalis/ui-kit/icons";
import { nextTick, ref, useTemplateRef } from "vue";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

const props = defineProps<{
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  confirm: [name: string];
  cancel: [];
}>();

const { t, k } = useTypedIntl();

const newCategoryName = ref<string>("");
const categoryNameInput = useTemplateRef<{ $el?: HTMLInputElement; focus?: () => void }>("categoryNameInput");

async function focusInput(): Promise<void> {
  await nextTick();

  const input = categoryNameInput.value;
  const element = input?.$el ?? input;

  element?.focus?.();
}

function confirmCategoryCreation() {
  const categoryName = newCategoryName.value.trim();

  if (!categoryName) return;

  emit("confirm", categoryName);
}

defineExpose({
  focusInput,
});
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex items-center gap-2 p-1">
      <InputText
        ref="categoryNameInput"
        v-model="newCategoryName"
        class="!h-[38px] min-w-0 flex-1 !rounded-[8px] !border-brand !px-3 !py-2 !text-sm !font-normal italic !text-text-secondary placeholder:text-tertiary placeholder:italic"
        autofocus
        :placeholder="t(k.tools.siteManagement.siteTreeStructure.newCategory)"
        :data-test-id="DATA_TEST_IDS.components.categoryCreationForm.input"
        :disabled="props.isLoading"
        @keydown.enter.prevent.stop="confirmCategoryCreation"
      />

      <HospitalityButton
        :aria-label="t(k.tools.siteManagement.siteTreeStructure.createCategory)"
        :data-test-id="DATA_TEST_IDS.components.categoryCreationForm.confirmButton"
        :disabled="props.isLoading"
        is-icon-only
        :loading="props.isLoading"
        size="xs"
        @click="confirmCategoryCreation"
      >
        <i class="pi pi-check" />
      </HospitalityButton>
    </div>

    <HospitalityButton
      class="!text-danger"
      :data-test-id="DATA_TEST_IDS.components.categoryCreationForm.cancelButton"
      :icon="TimesIcon"
      :label="t(k.global.misc.cancel)"
      severity="link"
      size="xs"
      @click="emit('cancel')"
    />
  </div>
</template>
